import type { ScopedThreadRef, ThreadId } from "@cmmd-forge/contracts";
import { scopeThreadRef } from "@cmmd-forge/client-runtime";
import type { SidebarThreadSortOrder } from "@cmmd-forge/contracts/settings";
import { getFallbackThreadIdAfterDelete } from "../components/Sidebar.logic";
import type { Thread } from "../types";

/**
 * Post-delete navigation contract:
 *  - `navigate-to-thread`: the user was on the deleted thread and there is a
 *    still-visible fallback in the same project.
 *  - `navigate-to-root`: the user was on the deleted thread and either no
 *    fallback id was selected, or the fallback id no longer resolves to a
 *    visible thread (archive race between snapshot and dispatch).
 *  - `no-op`: the user wasn't routed to the deleted thread, so the URL stays.
 */
export type PostDeleteNavigationDecision =
  | { readonly kind: "navigate-to-thread"; readonly ref: ScopedThreadRef }
  | { readonly kind: "navigate-to-root" }
  | { readonly kind: "no-op" };

export interface DeleteNavigationInput<
  T extends Pick<Thread, "id" | "projectId" | "createdAt" | "updatedAt"> & {
    readonly archivedAt?: string | null;
    readonly deletedAt?: string | null;
  },
> {
  /** Snapshot of threads visible at the time the delete was initiated. */
  readonly threads: readonly T[];
  /** The thread being deleted. */
  readonly deletedThreadRef: ScopedThreadRef;
  /** Where the SPA is currently routed; `null` if the user is on a non-thread URL. */
  readonly currentRouteThreadRef: ScopedThreadRef | null;
  /** All threads being deleted in this action (multi-select). May be empty. */
  readonly deletedThreadIds: ReadonlySet<ThreadId>;
  readonly sortOrder: SidebarThreadSortOrder;
  /**
   * Resolves a thread ref against the live store *after* the delete has been
   * dispatched. Returns `null` if the thread no longer exists. The caller
   * always provides this so we can re-validate the pre-dispatch fallback
   * pick before committing the navigation.
   */
  readonly lookupThreadByRef: (
    ref: ScopedThreadRef,
  ) => Pick<Thread, "id" | "environmentId" | "archivedAt"> | null;
}

export function pickPostDeleteNavigation<
  T extends Pick<Thread, "id" | "projectId" | "createdAt" | "updatedAt"> & {
    readonly archivedAt?: string | null;
    readonly deletedAt?: string | null;
  },
>(input: DeleteNavigationInput<T>): PostDeleteNavigationDecision {
  const { threads, deletedThreadRef, currentRouteThreadRef, deletedThreadIds, sortOrder } = input;

  const shouldNavigateAway =
    currentRouteThreadRef?.threadId === deletedThreadRef.threadId &&
    currentRouteThreadRef.environmentId === deletedThreadRef.environmentId;
  if (!shouldNavigateAway) return { kind: "no-op" };

  const fallbackThreadId = getFallbackThreadIdAfterDelete({
    threads,
    deletedThreadId: deletedThreadRef.threadId,
    deletedThreadIds,
    sortOrder,
  });
  if (!fallbackThreadId) return { kind: "navigate-to-root" };

  const fallbackRef = scopeThreadRef(deletedThreadRef.environmentId, fallbackThreadId);
  const fallbackThread = input.lookupThreadByRef(fallbackRef);
  // Post-dispatch re-check: between the snapshot and now, the fallback could
  // have been archived in another tab or by a sibling action. Treat archived
  // as a near-miss and fall back to root rather than landing on a row the
  // sidebar (correctly) hides.
  if (!fallbackThread || (fallbackThread.archivedAt ?? null) !== null) {
    return { kind: "navigate-to-root" };
  }

  return { kind: "navigate-to-thread", ref: fallbackRef };
}
